import React from 'react';
import PropTypes from 'prop-types';
import './renewalAdjustCover.scss';
import { DynamicSlider } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import OptionRow from '../../optionRow/optionRow';
import { defaultOptions } from './defaultOption';
import { RenewalDataPropType } from '../../../propTypes/renewalData';
import { QuoteDetailPropTypes } from '../../../propTypes/quoteDetails';
import { transformCMSRichText } from '../../../utils/transformContent';
import { RenewalAdjustPopup } from './renewalAdjustPopup';
import { PRODUCT_CODE } from '../../../constants/renewalHub';
import {
  RENEWAL_HUB_CONTENT,
  RENEWAL_HUB_MODULAR_CONTENT,
  RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
} from '../../../constants/cmsCodeName';
import { useRenewalAdjustCover } from './useRenewalAdjustCover';

export const RenewalAdjustCover = ({
  renewalSummaryData,
  renewalQuoteData,
  cmsData,
  whatsCoveredContent,
  generalTooltip
}) => {
  const {
    filteredExcessOptions,
    filteredLevelsOfCoverOptions,
    currentExcessCodeForSlider,
    currentLevelOfCoverCodeForSlider,
    handleChange,
    handleExcessSliderChange,
    handleLevelOfCoverSliderChange,
    popupState,
    selectedValues,
    handleClosePopup
  } = useRenewalAdjustCover(renewalSummaryData, renewalQuoteData);

  const optionalCoverTotals = (renewalQuoteData?.Pets?.[0]?.Premiums ?? [])
    .filter(
      (premium) =>
        premium.ProductCode === PRODUCT_CODE.CORE &&
        premium.ExcessCode === selectedValues.ExcessCode &&
        premium.LevelOfCoverCode === selectedValues.LevelOfCoverCode
    )
    .flatMap((premium) => premium.OptionalCovers ?? [])
    .reduce(
      (totals, cover) => {
        if (cover.ProductCode === PRODUCT_CODE.BOCA || cover.ProductCode === PRODUCT_CODE.ROCA) {
          totals[cover.ProductCode] +=
            (cover.TotalOptionalCoverAmount ?? 0) + (cover.TotalOptionalCoverLoadingAmount ?? 0);
        }
        return totals;
      },
      { [PRODUCT_CODE.BOCA]: 0, [PRODUCT_CODE.ROCA]: 0 }
    );

  const hasZeroBocaPricing = optionalCoverTotals[PRODUCT_CODE.BOCA] === 0;
  const hasZeroRocaPricing = optionalCoverTotals[PRODUCT_CODE.ROCA] === 0;

  const hideRocaOption =
    (renewalQuoteData?.IsNewBusinessApiError && !renewalSummaryData?.RoutineCare) || hasZeroRocaPricing;
  const hideBocaOption =
    (renewalQuoteData?.IsNewBusinessApiError && !renewalSummaryData?.BoosterCare) || hasZeroBocaPricing;

  const contentPlaceholderValues = renewalSummaryData || {};
  const { [RENEWAL_HUB_CONTENT.ADJUST_COVER]: adjustCover, [RENEWAL_HUB_CONTENT.OPTIONAL_EXTRAS]: optionalExtras } =
    cmsData || {};
  const {
    [RENEWAL_HUB_CONTENT.ROUTINE_CARE_CONTENT]: routineCareContent,
    [RENEWAL_HUB_CONTENT.BOOSTER_CARE_CONTENT]: boosterCareContent
  } = whatsCoveredContent?.content || {};
  const {
    [RENEWAL_HUB_CONTENT.EXCESS_TOOLTIP]: excessTooltip,
    [RENEWAL_HUB_CONTENT.BENEFIT_PERCENTAGE_TOOLTIP]: benefitPercentageTooltip,
    [RENEWAL_HUB_CONTENT.BOOSTER_CARE_TOOLTIP]: boosterCareTooltip,
    [RENEWAL_HUB_CONTENT.ROUTINE_CARE_TOOLTIP]: routineCareTooltip
  } = generalTooltip?.content || {};

  return (
    <div className="renewalAdjustCover">
      <h3>
        {transformCMSRichText(
          adjustCover?.[RENEWAL_HUB_MODULAR_CONTENT.ADJUST_COVER]?.heading,
          'Adjust cover',
          contentPlaceholderValues,
          RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
        )}
      </h3>

      <div className="label">
        {transformCMSRichText(
          adjustCover?.[RENEWAL_HUB_MODULAR_CONTENT.ADJUST_ANNUAL_EXCESS]?.value,
          'Adjust annual excess',
          contentPlaceholderValues,
          RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
          excessTooltip,
          undefined,
          true // lowercase placeholder values
        )}
      </div>
      <DynamicSlider
        id="adjustAnnualExcess"
        options={filteredExcessOptions}
        codeKey="excessCode"
        valueKey="excess"
        selectedCode={selectedValues.ExcessCode}
        currentCode={currentExcessCodeForSlider}
        formatValue={(value) => `$${value}`}
        onChange={handleExcessSliderChange}
      />

      <div className="label">
        {transformCMSRichText(
          adjustCover?.[RENEWAL_HUB_MODULAR_CONTENT.ADJUST_BENEFIT_PERCENTAGE]?.value,
          'Adjust benefit percentage',
          contentPlaceholderValues,
          RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
          benefitPercentageTooltip
        )}
      </div>

      <DynamicSlider
        id="levelOfCover"
        options={filteredLevelsOfCoverOptions}
        codeKey="levelOfCoverCode"
        valueKey="levelOfCover"
        selectedCode={selectedValues.LevelOfCoverCode}
        currentCode={currentLevelOfCoverCodeForSlider}
        formatValue={(value) => `${value}%`}
        onChange={handleLevelOfCoverSliderChange}
      />

      {(!hideRocaOption || !hideBocaOption) && (
        <h3>
          {transformCMSRichText(
            optionalExtras?.[RENEWAL_HUB_MODULAR_CONTENT.OPTIONAL_EXTRAS]?.heading,
            'Optional extras',
            contentPlaceholderValues,
            RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
          )}
        </h3>
      )}

      {!hideRocaOption && (
        <>
          <div className="label">
            {transformCMSRichText(
              optionalExtras?.[RENEWAL_HUB_MODULAR_CONTENT.ROUTINE_CARE]?.value,
              'Include Routine Care?',
              contentPlaceholderValues,
              RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
              routineCareTooltip,
              routineCareContent
            )}
          </div>
          <OptionRow
            options={defaultOptions.routineCare}
            defaultValue={renewalSummaryData?.RoutineCare}
            selectedValue={selectedValues.RoutineCare}
            onChange={(value) => handleChange('RoutineCare', value)}
          />
        </>
      )}

      {!hideBocaOption && (
        <>
          <div className="label">
            {transformCMSRichText(
              optionalExtras?.[RENEWAL_HUB_MODULAR_CONTENT.BOOSTER_CARE_OR_EXTRA_CARE]?.value,
              'Include Booster Care?',
              contentPlaceholderValues,
              RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
              boosterCareTooltip,
              boosterCareContent
            )}
          </div>
          <OptionRow
            options={defaultOptions.boosterCare}
            defaultValue={renewalSummaryData?.BoosterCare}
            selectedValue={selectedValues.BoosterCare}
            onChange={(value) => handleChange('BoosterCare', value)}
          />
        </>
      )}

      <RenewalAdjustPopup
        isOpen={popupState.isOpen}
        onClose={handleClosePopup}
        title={popupState.title}
        body={popupState.body}
        renewalSummaryData={renewalSummaryData}
      />
    </div>
  );
};

RenewalAdjustCover.propTypes = {
  renewalSummaryData: RenewalDataPropType,
  // eslint-disable-next-line react/forbid-prop-types
  renewalQuoteData: PropTypes.shape(QuoteDetailPropTypes),
  // eslint-disable-next-line react/forbid-prop-types
  cmsData: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  whatsCoveredContent: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  generalTooltip: PropTypes.any
};
